<template>
  <div class="container">
    <div class="spinner" v-if="!showLogin"></div>
    <div class="message">{{ informationMessage }}</div>
    <button class="btn btn-primary margin-top-1" @click="goToLogin()" v-if="showLogin">Go to Login</button>
  </div>
</template>

<script setup>
import { inject, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useIdentityStore } from '@/stores/identity'
import { getIntermediary } from '@/services/implementationmanagement.api.js'

const msalInstance = inject('msalInstance')
const identityStore = useIdentityStore()
const router = useRouter()
const showLogin = ref(false)
const informationMessage = ref('Loading your account...')

const AUTO_LOGIN_KEY = 'account-login-auto-redirect-started'

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function getAccount() {
  return (
    msalInstance?.getActiveAccount() ||
    msalInstance?.getAllAccounts?.()[0] ||
    null
  )
}

function isInteractionInProgressError(error) {
  const code = error?.errorCode || error?.code || ''
  const message = String(error?.message || '').toLowerCase()

  return (
    code === 'interaction_in_progress' ||
    message.includes('interaction_in_progress')
  )
}

onMounted(async () => {
  if (!msalInstance) {
    informationMessage.value = 'Authentication is unavailable.'
    return
  }

  try {

      const logoutInProgress = sessionStorage.getItem('logout-in-progress') === 'true'


      if (logoutInProgress) {
        sessionStorage.removeItem('logout-in-progress')
        informationMessage.value = 'You have been signed out.'
        showLogin.value = true
        return
      }


    // Give app bootstrap a moment to finish redirect handling and account restore
    await sleep(300)

    let account = getAccount()

    if (account) {
      await finishLogin(account)
      return
    }

    informationMessage.value = 'Redirecting you to login...'

    // Prevent repeated auto-redirect attempts on the same visit
    const alreadyStarted = sessionStorage.getItem(AUTO_LOGIN_KEY) === 'true'
    if (alreadyStarted) {
      informationMessage.value = 'Finalising sign-in...'
      return
    }

    sessionStorage.setItem(AUTO_LOGIN_KEY, 'true')

    await msalInstance.loginRedirect({
      scopes: ['openid', 'profile', 'offline_access'],
      prompt: 'login',
      extraQueryParameters: { p: 'OneTimeCodeLogin' }
    })
  } catch (error) {
    console.error('Login failed:', error)

    if (isInteractionInProgressError(error)) {
      informationMessage.value = 'Finalising sign-in...'
      return
    }

    sessionStorage.removeItem(AUTO_LOGIN_KEY)
    informationMessage.value = 'Login failed. Please refresh and try again.'
  }
})

async function goToLogin() {
  await msalInstance.loginRedirect({
    scopes: ['openid', 'profile', 'offline_access'],
    prompt: 'login',
    extraQueryParameters: { p: 'OneTimeCodeLogin' }
  })
}

async function finishLogin(account) {
  msalInstance.setActiveAccount(account)

  const tokenResult = await msalInstance.acquireTokenSilent({
    account,
    scopes: ['openid', 'profile', 'email'],
    extraQueryParameters: { p: 'OneTimeCodeLogin' }
  })

  const claims = tokenResult?.idTokenClaims || account.idTokenClaims || null

  if (!areLoginClaimsValid(claims)) {
    informationMessage.value = 'A failure has occurred during login.'
    sessionStorage.removeItem(AUTO_LOGIN_KEY)
    return
  }

  identityStore.setUser(account, claims)
  sessionStorage.removeItem(AUTO_LOGIN_KEY)

if (claims.userType === 'Direct') {
  const associatedClients = Array.isArray(claims.associatedClients)
    ? claims.associatedClients
    : claims.associatedClients
      ? [claims.associatedClients]
      : [];

  if (associatedClients.length !== 1) {
    informationMessage.value = 'Direct users must be associated with exactly one client.';
    return;
  }

  const firstClient = associatedClients[0];

  const clientId =
    typeof firstClient === 'string'
      ? firstClient
      : firstClient?.clientId || firstClient?.id || null;

  if (!clientId) {
    informationMessage.value = 'Unable to determine your client.';
    return;
  }

  await identityStore.selectClient(clientId);
  await router.replace('/');
  return;
}

  if (claims.userType === 'Intermediary') {
    const response = await getIntermediary(claims.oid)

    if (response.status !== 200 || !response.data) {
      informationMessage.value = 'Error loading intermediary details.'
      return
    }

    identityStore.setIntermediary(response.data)
    await router.replace('/clients')
    return
  }

  if (claims.userType === 'Administrator') {
    await router.replace('/admin/clients')
    return
  }

  informationMessage.value = 'Your account type is not recognised.'
}

function isGuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function areLoginClaimsValid(claims) {
  if (!claims) return false;

  if (claims.userType === 'Administrator') return true;
  if (claims.userType === 'Intermediary') return true;

  if (claims.userType === 'Direct') {
    const clients = claims.associatedClients;

    if (Array.isArray(clients)) {
      return clients.length > 0;
    }

    return typeof clients === 'string' && clients.trim().length > 0;
  }

  return false;
}
</script>

<style scoped>
body {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: linear-gradient(to right, #e0eafc, #cfdef3);
  color: #333;
}

.container {
  text-align: center;
}

.spinner {
  margin: 50px auto;
  width: 60px;
  height: 60px;
  border: 8px solid #ccc;
  border-top: 8px solid rgb(255, 90, 80);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.message {
  font-size: 1.8rem;
  font-weight: 500;
  margin-top: 20px;
}
</style>